<template>
  <b-modal
      id="image-upload"
      ref="image-upload"
      centered
      footer-class="flex-nowrap"
      no-close-on-backdrop
      :size="[1,2].includes(activeTab) ? 'xl' : 'md'"
      @ok="confirmUpload"
  >
    <template #modal-header>
      <h3>{{ modalTitle }}</h3>
    </template>
    <div v-if="!isSysAdmin">
      <image-upload
          ref="imageUpload"
          :image-object="imageObjectOfModalWindow"
          :allowFileSizeValidation="allowFileSizeValidation"
          :acceptedFileTypes="typeOfUpload"
          :type="type"
          @upload-image-object="updateUploadImageObject"
          @disable-modal-ok-button="disableOkButtonManually"
          @file-removed="removeImage"
      />
    </div>
    <b-tabs v-else v-model="activeTab" class="mb-3">
      <b-tab :title="$t('modals.imageUpload.tabs.upload')">
        <div>
          <div class="mx-3 mt-3" v-if="usePersonalization === true">
            <p>{{ $t('modals.imageUpload.imageTitle') }}</p>
          </div>
          <image-upload
              ref="imageUpload"
              :image-object="imageObjectOfModalWindow"
              :allowFileSizeValidation="allowFileSizeValidation"
              :acceptedFileTypes="typeOfUpload"
              :type="type"
              @upload-image-object="updateUploadImageObject"
              @disable-modal-ok-button="disableOkButtonManually"
              @file-removed="removeImage"
          />
          <div class="m-3 align-right" v-if="hasPersonalization">
            <button
                class="btn green-button-transparent border-0 btn-secondary"
                v-if="usePersonalization === false"
                @click="usePersonalization = true"
            >
              {{ $t('modals.imageUpload.usePersonalization') }}
            </button>
            <button
                class="btn green-button-transparent border-0 btn-secondary"
                v-if="usePersonalization === true"
                @click="disablePersonalization"
            >
              {{ $t('modals.imageUpload.disablePersonalization') }}
            </button>
          </div>
          <div v-if="usePersonalization">
            <div class="m-3" v-if="personalizationSetIdArray">
              <b-form-select
                  v-model="setId"
                  :options="personalizationSetIdArray"
                  @change="valueId = null"
              ></b-form-select>
            </div>
            <div class="m-3" v-if="setId">
              <b-form-select
                  v-model="valueId"
                  :options="personalizationValueArray"
                  @change="updatePersonalization"
              ></b-form-select>
            </div>
          </div>
        </div>
      </b-tab>
      <b-tab :title="$t('modals.imageUpload.tabs.library')">
        <div class="p-3">
          <div v-if="libraryLoading">{{$t('modals.imageUpload.libraryLoading')}}</div>
          <div v-else-if="libraryImages.length === 0">{{$t('modals.imageUpload.libraryEmpty')}}</div>
          <div v-else>
            <div class="image-library-grid">
              <div v-for="img in libraryImages" :key="img.image_id" :class="['image-library-item', { selected: selectedLibraryImageId === img.image_id }]" @click="selectLibraryImage(img)">
                <div class="image-thumb-container">
                  <img :src="cloudImageUrl(img.url)" :alt="img.name" class="img-thumbnail" />
                </div>
                <div class="image-name">{{ img.name }}</div>
              </div>
            </div>
            <b-pagination
              v-if="libraryImages.length > 0 && totalPages > 1"
              v-model="currentPage"
              :per-page="1"
              :total-rows="totalPages"
              :limit="5"
              size="md"
              @input="fetchLibraryImages(true, $event)"
              class="library-pagination"
            />
          </div>
        </div>
      </b-tab>
      <b-tab :title="$t('modals.imageUpload.tabs.pexels')">
        <div class="p-3">
          <div class="d-flex align-items-center mb-3">
            <a href="https://www.pexels.com" target="_blank" class="mr-3">
              <img src="https://images.pexels.com/lib/api/pexels.png" alt="Pexels" height="38" />
            </a>
            <form @submit.prevent="searchPexels" class="flex-grow-1">
              <div class="d-flex">
                <input v-model="pexelsQuery" type="text" class="form-control mr-2" :placeholder="$t('modals.imageUpload.pexelsSearchPlaceholder')" />
                <b-button type="submit" variant="success">{{$t('buttons.search')}}</b-button>
              </div>
            </form>
          </div>
          <div v-if="pexelsLoading" class="text-center my-4">
            <b-spinner small type="grow" /> {{$t('modals.imageUpload.pexelsLoading')}}
          </div>
          <div v-if="pexelsError" class="text-danger my-2">{{ pexelsError }}</div>
          <div v-if="pexelsImages.length > 0" class="image-library-grid">
            <div v-for="img in pexelsImages" :key="img.id" :class="['image-library-item', { selected: selectedPexelsImageId === img.id }]">
              <div :id="`pexels-img-${img.id}`" class="image-thumb-container" @click="uploadPexelsImage(img)">
                <img :src="img.src.medium" :alt="img.alt || 'Pexels image'" class="img-thumbnail" />
                <div v-if="pexelsUploadingId === img.id" class="pexels-uploading-overlay">
                  <b-spinner small type="grow" />
                </div>
              </div>
              <b-tooltip :target="`pexels-img-${img.id}`" triggers="hover" placement="top">
                {{ $t('modals.imageUpload.pexelsPhotoBy', { photographer: img.photographer }) }}
              </b-tooltip>
              <div class="image-name">{{ img.alt }}</div>
            </div>
          </div>
          <div v-if="pexelsImages.length === 0 && !pexelsLoading && pexelsQuery">{{$t('modals.imageUpload.pexelsNoResults')}}</div>
          <b-pagination
            v-if="pexelsImages.length > 0 && pexelsTotalPages > 1"
            v-model="pexelsCurrentPage"
            :per-page="1"
            :total-rows="pexelsTotalPages"
            :limit="5"
            size="md"
            @input="searchPexels($event)"
            class="library-pagination"
          />
        </div>
      </b-tab>
    </b-tabs>
    <template #modal-footer="{ close }">
      <b-button
          class="p-3 border-right-light-grey"
          block
          @click="close()"
      >
        {{ $t('buttons.cancel') }}
      </b-button>
      <b-button
          variant="primary"
          class="p-3"
          block
          @click="confirmUpload"
          :disabled="computedDisableButton"
      >
        {{ $t('buttons.ok') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { cloneDeep } from 'lodash';
import GetImagesByGroupId from '@/graphQlQueries/queries/getImagesByGroupId';
import { BPagination } from 'bootstrap-vue';

export default {
  name: 'ModalUploadingImage',
  components: {
    ImageUpload: () => import('@/components/helper/ImageUpload.vue'),
    BPagination,
  },
  props: {
    isSysAdmin: {
      type: Boolean,
      required: false,
      default: false,
    },
    personalization: {
      type: Array,
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: null,
    },
    imageObject: {
      type: Object,
      required: false,
      default() {
        return {
          url: null,
          width: null,
          height: null,
          focalpoint: {
            x: 50,
            y: 50,
          },
        };
      },
    },
    groupId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    imageReturnObj: null,
    disableOkButton: true,
    usePersonalization: false,
    setId: null,
    valueId: null,
    activeTab: 0,
    libraryImages: [],
    libraryLoading: false,
    selectedLibraryImageId: null,
    currentPage: 1,
    totalPages: 1,
    pageSize: 12,
    pexelsQuery: '',
    pexelsImages: [],
    pexelsLoading: false,
    pexelsError: '',
    selectedPexelsImageId: null,
    pexelsUploadingId: null,
    pexelsCurrentPage: 1,
    pexelsTotalPages: 1,
  }),
  watch: {
    imageObject(newObject) {
      this.setupUploadImageModal(newObject);
    },
    activeTab(newTab) {
      if (newTab === 1) {
        this.currentPage = 1;
        this.fetchLibraryImages(true, 1);
      }
    },
  },
  mounted() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'image-upload') {
        this.disableOkButtonManually(true);
        this.activeTab = 0;
      }
    });
  },
  computed: {
    hasPersonalization() {
      if (this.personalization) {
        return this.personalization.length > 0;
      }
      return false;
    },
    personalizationSetIdArray() {
      const personalizationSetIdArray = [];
      if (this.personalization) {
        personalizationSetIdArray.push({
          value: null,
          text: this.$t('modals.imageUpload.selectOption'),
        });
        this.personalization.forEach((personalizationSet) => {
          // check first if the schema contains a mediaField
          if (personalizationSet.schema) {
            const mediaField = personalizationSet.schema.find(
              (schemaObj) => schemaObj.type === 'MediaField',
            );
            if (mediaField) {
              personalizationSetIdArray.push({
                value: personalizationSet.personalized_variable_set_id,
                text: personalizationSet.name,
              });
            }
          }
        });
        return personalizationSetIdArray;
      }
      return null;
    },
    personalizationValueArray() {
      const personalizationValueArray = [];
      if (this.setId) {
        const personalizationSet = this.personalization.find(
          // eslint-disable-next-line max-len
          (personalizationSetObj) => personalizationSetObj.personalized_variable_set_id === this.setId,
        );
        if (personalizationSet) {
          personalizationValueArray.push({
            value: null,
            text: this.$t('modals.imageUpload.selectOption'),
          });
          // find the media attribute
          const mediaFieldArray = personalizationSet.schema.filter(
            (schemaObj) => schemaObj.type === 'MediaField',
          ).map((mediaObj) => ({ value: mediaObj.id, text: mediaObj.name }));
          return [
            {
              value: null,
              text: this.$t('modals.imageUpload.selectOption'),
            },
            ...mediaFieldArray,
          ];
        }
      }
      return null;
    },
    modalTitle() {
      switch (this.type) {
        case 'audio':
          return this.$t('modals.imageUpload.audioTitle');
        case 'video' || 'video_webm':
          return this.$t('modals.imageUpload.videoTitle');
        case 'pdf':
          return this.$t('modals.imageUpload.pdfTitle');
        default:
          return this.$t('modals.imageUpload.imageTitle');
      }
    },
    imageObjectOfModalWindow() {
      return this.imageObject;
    },
    typeOfUpload() {
      if (this.type === 'audio') {
        return 'audio/mp3,audio/mpeg';
      }
      if (this.type === 'video') {
        return 'video/mp4';
      }
      if (this.type === 'video_webm') {
        return 'video/webm';
      }
      if (this.type === 'pdf') {
        return 'application/pdf';
      }
      return 'image/png,image/jpeg,image/gif,image/svg+xml';
    },
    allowFileSizeValidation() {
      return this.type === 'audio' || this.type === 'video' || this.type === 'video_webm' || this.type === 'pdf';
    },
    computedDisableButton() {
      if (this.valueId && this.setId && this.imageReturnObj?.url) {
        return false;
      }
      return this.disableOkButton;
    },
  },
  methods: {
    removeImage() {
      if (this.imageReturnObj) {
        this.imageReturnObj.url = null;
      }
      this.disableOkButton = true;
    },
    setupUploadImageModal(newObject) {
      this.setId = null;
      this.valueId = null;
      this.usePersonalization = false;
      if (newObject.personalization && this.personalization) {
        const personalizationSetIndex = this.personalization.findIndex(
          // eslint-disable-next-line max-len
          (persoSet) => persoSet.personalized_variable_set_id === newObject.personalization.setId,
        );
        if (personalizationSetIndex !== -1
          && this.personalization[personalizationSetIndex].schema
        ) {
          if (this.personalization[personalizationSetIndex].schema.find(
            (elem) => elem.id === newObject.personalization.valueId,
          )) {
            this.usePersonalization = true;
            this.setId = newObject.personalization.setId;
            this.valueId = newObject.personalization.valueId;
          }
        }
      }
      this.imageReturnObj = cloneDeep(this.imageObject);
    },
    disablePersonalization() {
      this.usePersonalization = false;
      this.valueId = null;
      this.setId = null;
      if (this.imageReturnObj) {
        delete this.imageReturnObj.personalization;
      }
      delete this.imageReturnObj.personalization;
      if (this.imageReturnObj?.url) {
        this.disableOkButton = false;
      }
    },
    updateUploadImageObject(imageObj) {
      this.imageReturnObj = imageObj;
      if (this.setId && this.valueId) {
        this.imageReturnObj.personalization = {
          setId: this.setId,
          valueId: this.valueId,
        };
      } else {
        delete this.imageReturnObj.personalization;
      }
      this.disableOkButton = imageObj === null;
    },
    disableOkButtonManually(disableButton) {
      if (disableButton) {
        this.disableOkButton = true;
      }
    },
    updatePersonalization(value) {
      if (value) {
        this.imageReturnObj.personalization = {
          setId: this.setId,
          valueId: value,
        };
      } else {
        delete this.imageReturnObj.personalization;
      }
    },
    confirmUpload() {
      if (this.activeTab === 1 && this.selectedLibraryImageId) {
        // Library tab: emit the selected image
        this.$emit('image-uploaded', this.imageReturnObj);
      } else if (this.activeTab === 0) {
        // Upload tab: emit the uploaded image
        this.$emit('image-uploaded', this.imageReturnObj);
      }
      this.$refs['image-upload'].hide();
      this.imageReturnObj = null;
      this.selectedLibraryImageId = null;
    },
    cancelModal() {
      this.$refs['image-upload'].hide();
      this.imageReturnObj = null;
      this.$emit('cancel-modal');
    },
    async fetchLibraryImages(force = false, page = this.currentPage) {
      this.libraryLoading = true;
      try {
        const offset = (page - 1) * this.pageSize;
        const { data } = await this.$apollo.query({
          query: GetImagesByGroupId,
          variables: { group_id: this.groupId, limit: this.pageSize, offset },
          fetchPolicy: force ? 'network-only' : 'cache-first',
        });
        this.libraryImages = data.images || [];
        // If the backend returns total count, use it. Otherwise, estimate.
        // For now, estimate totalPages by checking if we got a full page.
        this.totalPages = (this.libraryImages.length === this.pageSize) ? page + 1 : page;
        this.currentPage = page;
      } catch (e) {
        this.libraryImages = [];
      }
      this.libraryLoading = false;
    },
    selectLibraryImage(img) {
      this.selectedLibraryImageId = img.image_id;
      this.imageReturnObj = {
        url: img.url,
        name: img.name,
        group_id: this.groupId,
        author: img.author,
        metadata: img.metadata,
        created_at: img.created_at,
      };
      this.disableOkButton = false;
    },
    goToLibraryPage(page) {
      if (page < 1) return;
      this.fetchLibraryImages(true, page);
    },
    cloudImageUrl(url) {
      if (!url) return '';
      return `https://axyqwmwryo.cloudimg.io/v7/${url}?ci_url_encoded=1&org_if_sml=1&alt=media&func=bound&q=95&w=262`;
    },
    async searchPexels(page = 1) {
      if (!this.pexelsQuery) {
        this.pexelsImages = [];
        return;
      }
      this.pexelsLoading = true;
      this.pexelsError = '';
      try {
        // Replace with your real Pexels API key
        const API_KEY = '563492ad6f9170000100000125898ee303914aa69100aedd979d63ad';
        const response = await fetch(`https://api.pexels.com/v1/search?query=${encodeURIComponent(this.pexelsQuery)}&per_page=12&page=${page}`, {
          headers: { Authorization: API_KEY },
        });
        if (!response.ok) throw new Error('Pexels API error');
        const data = await response.json();
        this.pexelsImages = data.photos || [];
        this.pexelsCurrentPage = page;
        // Pexels API returns total_results, so we can calculate total pages
        if (data.total_results) {
          this.pexelsTotalPages = Math.ceil(data.total_results / 12);
        } else {
          this.pexelsTotalPages = (data.photos.length === 12) ? page + 1 : page;
        }
      } catch (e) {
        this.pexelsError = this.$t('modals.imageUpload.pexelsError');
      }
      this.pexelsLoading = false;
    },
    async uploadPexelsImage(img) {
      this.pexelsUploadingId = img.id;
      this.selectedPexelsImageId = img.id;
      this.disableOkButton = true;
      try {
        // Download the image as a blob
        const response = await fetch(img.src.original);
        const blob = await response.blob();
        const fileName = `${img.alt || 'pexels-image'}.jpg`;
        const file = new File([blob], fileName, { type: blob.type });
        // Switch to the Upload tab
        this.activeTab = 0;
        // Wait for the tab to render
        await this.$nextTick();
        // Add the file to the filepond uploader
        console.log(this.$refs.imageUpload);
        console.log(this.$refs.imageUpload.$refs.pond);
        if (this.$refs.imageUpload && this.$refs.imageUpload.$refs.pond) {
          await this.$refs.imageUpload.$refs.pond.addFile(file);
        }
        // Optionally, you can set a flag or message to indicate success
        this.disableOkButton = false;
      } catch (e) {
        this.pexelsError = this.$t('modals.imageUpload.pexelsUploadError');
        this.disableOkButton = true;
      }
      this.pexelsUploadingId = null;
    },
  },
};
</script>

<style scoped lang="scss">
.delete-icon svg {
  font-size: 80px;
}

/deep/ button.disabled {
  cursor: not-allowed;
}

.image-library-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.image-library-item {
  width: 262px;
  cursor: pointer;
  text-align: center;
}
.image-library-item.selected {
  border: 2px solid #007bff;
  background: #eaf4ff;
}
.image-thumb-container {
  background: #f0f0f0;
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.img-thumbnail {
  max-width: 100%;
  max-height: 180px;
  display: block;
  margin: 0 auto;
}
.image-name {
  font-size: 0.85em;
  margin-top: 0.5em;
  color: #555;
}
.library-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.5em;
  gap: 1em;
}

// Modern tab styling
/deep/ .nav-tabs {
  border-bottom: none;
  background: #fff;
  padding: 0.5em 0.5em 0 0.5em;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: flex;
  gap: 1.5em;
}
/deep/ .nav-tabs .nav-item {
  margin-bottom: 0;
}
/deep/ .nav-tabs .nav-link {
  border: none;
  background: transparent;
  color: #888;
  font-weight: 500;
  font-size: 1.08em;
  padding: 0.7em 1.6em;
  border-radius: 8px 8px 0 0;
  margin-right: 0.2em;
  transition: background 0.2s, color 0.2s;
}
/deep/ .nav-tabs .nav-link.active {
  background: #34363a;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(52,54,58,0.08);
  position: relative;
  z-index: 2;
}
/deep/ .nav-tabs .nav-link:not(.active):hover {
  background: #f5f5f7;
  color: #222;
}
/deep/ .tab-content {
  background: #fff;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding-top: 1em;
}

/deep/ .pagination .page-item.active .page-link {
  background-color: #28dc8e !important;
  border-color: #28dc8e !important;
  color: #fff !important;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(40,220,142,0.08);
}
/deep/ .pagination .page-link {
  color: #28dc8e;
  border-radius: 6px;
  transition: background 0.2s, color 0.2s;
}
/deep/ .pagination .page-link:hover {
  background: #e6f9f1;
  color: #28dc8e;
}

// Add overlay style for uploading indicator
.pexels-uploading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 2;
}
</style>
